import { FC } from 'react';
import { BooleanField, ReferenceManyField, TextField } from 'react-admin';

import {
  GetInstrumentDocumentsFilters,
  GetInstrumentDocumentsQuery,
  GetInstrumentDocumentsResponseOne,
  getInstrumentDocumentsSortColumns,
} from '@Common/types/bo/instruments';

import { DatagridTyped, DeleteButton, EmptyScreen, Pagination } from '@sh4res/bo-common/components';
import { IdField, UTCDateField } from '@sh4res/bo-common/fields';
import { DocumentListViewerLink } from '@sh4res/bo-common/fields/DocumentListViewerLink';
import { EmptyFolderSvg } from '@sh4res/bo-common/graphics';
import { useCanAccess } from '@sh4res/bo-common/hooks';
import { ColumnList, RaSort } from '@sh4res/bo-common/utils';
import { isBoExternal } from '@sh4res/bo-common/utils/env';

const NoDocument = () => (
  <EmptyScreen label="resources.bo_common.instruments.documents.empty" svg={<EmptyFolderSvg />} />
);

const defaultSort: RaSort<GetInstrumentDocumentsQuery> = { field: 'type', order: 'DESC' };

const displayedColumns: (keyof GetInstrumentDocumentsResponseOne)[] = [
  'id',
  'url',
  'language',
  'archived',
  'created_at',
  'instrument_id',
];

const columns: ColumnList<GetInstrumentDocumentsResponseOne> = {
  // eslint-disable-next-line @sh4res/jsx-id-field-for-resources -- This is a false positive, this resource don't have a show page
  id: <TextField data-testid="BoExternalInstrumentDocumentsList_IdField" />,
  type: <TextField data-testid="BoExternalInstrumentDocumentsList_TypeField" />,
  language: <TextField data-testid="BoExternalInstrumentDocumentsList_LanguageField" />,
  instrument_id: (
    <IdField
      reference="instruments"
      data-testid="BoExternalInstrumentDocumentsList_InstrumentIdField"
    />
  ),
  url: (
    <DocumentListViewerLink<GetInstrumentDocumentsResponseOne> urlSource="url" nameSource="id" />
  ),
  archived: (
    <BooleanField
      label="resources.bo_common.instruments.documents.labels.is_archived"
      data-testid="BoExternalInstrumentDocumentsList_ArchivedField"
    />
  ),
  is_public: <BooleanField data-testid="BoExternalInstrumentDocumentsList_IsPublicField" />,
  created_at: (
    <UTCDateField
      showTime={!isBoExternal()}
      label="resources.bo_common.instruments.documents.labels.created_at"
      data-testid="BoExternalInstrumentDocumentsList_CreatedAtField"
    />
  ),
  updated_at: (
    <UTCDateField
      showTime={!isBoExternal()}
      data-testid="BoExternalInstrumentDocumentsList_UpdatedAtField"
    />
  ),
};

export const InstrumentDocumentsList: FC<{
  type: GetInstrumentDocumentsFilters['type'];
}> = ({ type }) => {
  const canDeleteInstrumentDocument = useCanAccess({
    resource: 'instruments/documents',
    action: 'delete',
  });

  const canDelete =
    canDeleteInstrumentDocument &&
    type === 'complexity_and_target_market_assessment' &&
    !isBoExternal();

  const deleteBtn = (
    <DeleteButton
      confirmTitle="resources.bo_common.instruments/documents.delete_confirm.title"
      confirmContent="resources.bo_common.instruments/documents.delete_confirm.content"
      confirmColor="warning"
      mutationMode="pessimistic"
      redirect={false}
      permissions={{
        resource: 'instruments/documents',
        action: 'delete',
      }}
      testId="BoExternalInstrumentDocumentsList_DeleteButton"
    />
  );

  return (
    <ReferenceManyField
      label="resources.bo_common.instruments.documents.title"
      reference="instruments/documents"
      source="id"
      target="instrument_id"
      sort={defaultSort}
      filter={{ type }}
      pagination={<Pagination limit={<></>} />}
      data-testid="BoExternalInstrumentDocumentsList_DocumentsList">
      <DatagridTyped<GetInstrumentDocumentsResponseOne & { actions?: boolean }>
        sortableColumns={getInstrumentDocumentsSortColumns}
        columnRegistry={{
          ...columns,
          ...(canDelete ? { actions: deleteBtn } : { actions: <></> }),
        }}
        displayedColumns={[...displayedColumns, ...(canDelete ? (['actions'] as const) : [])]}
        bulkActionButtons={false}
        empty={<NoDocument />}
      />
    </ReferenceManyField>
  );
};
